import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import useScrollReveal from "../hooks/useScrollReveal";
import styles from "../styles/HomePage.module.css";
import { FaCalendarAlt } from "react-icons/fa";
import Seo from "../components/seo";
import IndustriesHome from "../components/IndustriesHome";
import { Handshake, Users, Rocket, Globe2, Award, PhoneCall } from "lucide-react";
import {
  Target,
  PenTool,
  Zap,
  CalendarCheck,
  CalendarX,
  Timer,
} from "lucide-react";
import {
  ScanFace,
  UserCheck,
} from "lucide-react";
import {
  Compass,
  ShieldCheck,
  
  Crosshair,
  
  MonitorCheck, UsersRound, TrendingUp,
  Trophy, Layers, MailOpen,
} from "lucide-react";

const Homepage = () => {
  return (
    <main>
      <Seo
        title="InviteEngine | B2B Lead Generation & Outreach for UK SMBs"
        description="Fill your funnel with high-quality B2B sales conversations. InviteEngine provides smart targeting and outcome-driven outreach for ROI-focused UK SMBs."
        path="/"
      >
        {/* The Schema Script goes here */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "InviteEngine",
            url: "https://www.inviteengine.com/",
            logo: "https://www.inviteengine.com/logo.png",
            description:
              "B2B Lead Generation and Outreach Specialists helping UK SMBs build authentic sales pipelines.",
            address: {
              "@type": "PostalAddress",
              addressCountry: "Worldwide",
            },
            areaServed: {
              "@type": "Country",
              name: "Worldwide",
            },
            serviceType: [
              "B2B Lead Generation",
              "Email Outreach",
              "Cold Calling",
              "Sales Development",
            ],
          })}
        </script>
      </Seo>

      <section className={styles.hero}>{/* Your hero content... */}</section>
    </main>
  );
};

/* ══════════════════════════════
   ANIMATED COUNTER HOOK
══════════════════════════════ */
function useCountUp(target, duration = 2000, isVisible = false) {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const steps = 60;
    const increment = target / steps;
    const stepTime = duration / steps;
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else setCount(Math.floor(start));
    }, stepTime);
    return () => clearInterval(timer);
  }, [isVisible, target, duration]);
  return count;
}

/* ══════════════════════════════
   TESTIMONIALS
══════════════════════════════ */
const TESTIMONIALS = [
  {
    quote:
      "We went from zero pipeline to 18 qualified calls in our first month. The team's targeting was spot on.",
    name: "Sarah Mitchell",
    role: "VP of Sales",
    company: "TechFlow SaaS",
    initial: "S",
    color: "#03bfcb",
    metric: "18 meetings",
    metricLabel: "Month 1",
  },
  {
    quote:
      "The outcome-based model was a game-changer. We only paid for meetings that actually showed up. ROI was immediate.",
    name: "James Okafor",
    role: "CEO",
    company: "Pinnacle Consulting",
    initial: "J",
    color: "#004866",
    metric: "4.2x ROI",
    metricLabel: "First Quarter",
  },
  {
    quote:
      "Best outreach team we have ever worked with. They understood our ICP better than most of our own reps within the first week.",
    name: "Priya Sharma",
    role: "Head of Growth",
    company: "Nexus Analytics",
    initial: "P",
    color: "#7c3aed",
    metric: "31 calls",
    metricLabel: "6 Weeks",
  },
  {
    quote:
      "We had tried 3 agencies before InviteEngine. This is the first time we have seen consistent, qualified pipeline.",
    name: "Tom Keller",
    role: "CRO",
    company: "Brightpath SaaS",
    initial: "T",
    color: "#059669",
    metric: "3x Pipeline",
    metricLabel: "vs. Before",
  },
  {
    quote:
      "The transparency is unreal. We see every email, every reply, every booked call in real time.",
    name: "Aisha Patel",
    role: "Director of Sales",
    company: "CloudWave",
    initial: "A",
    color: "#dc2626",
    metric: "22 booked",
    metricLabel: "Month 2",
  },
  {
    quote:
      "Launch to first booked meeting took 4 days. I have never seen a vendor move that fast with that level of quality.",
    name: "Marcus Reid",
    role: "Founder",
    company: "RevOps Labs",
    initial: "M",
    color: "#d97706",
    metric: "4 days",
    metricLabel: "First Meeting",
  },
];

function TestimonialsCarousel() {
  const [active, setActive] = React.useState(0);
  const [animating, setAnimating] = React.useState(false);
  const [direction, setDirection] = React.useState("next");
  const intervalRef = React.useRef(null);

  const go = React.useCallback(
    (idx, dir) => {
      if (animating) return;
      clearInterval(intervalRef.current);
      setDirection(dir);
      setAnimating(true);
      setTimeout(() => {
        setActive(idx);
        setAnimating(false);
      }, 380);
    },
    [animating],
  );

  const next = React.useCallback(
    () => go((active + 1) % TESTIMONIALS.length, "next"),
    [active, go],
  );
  const prev = React.useCallback(
    () => go((active - 1 + TESTIMONIALS.length) % TESTIMONIALS.length, "prev"),
    [active, go],
  );

  React.useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (!animating) next();
    }, 5500);

    return () => clearInterval(intervalRef.current);
  }, [next, animating]);

  const t = TESTIMONIALS[active];

  return (
    <div className={styles.carousel}>
      <div className={styles.bgQuote}>"</div>
      <div className={styles.carouselInner}>
        <div
          className={`${styles.carouselCard} ${animating ? styles["slide_" + direction] : ""}`}
        >
          <div className={styles.carouselTop}>
            <div className={styles.starsAnimated}>
              {[0, 1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  className={styles.star}
                  style={{ animationDelay: i * 0.08 + "s" }}
                >
                  ★
                </span>
              ))}
            </div>
            <div
              className={styles.metricBadge}
              style={{ borderColor: t.color }}
            >
              <span className={styles.metricNum} style={{ color: t.color }}>
                {t.metric}
              </span>
              <span className={styles.metricLabel}>{t.metricLabel}</span>
            </div>
          </div>
          <blockquote className={styles.carouselQuote}>"{t.quote}"</blockquote>
          <div className={styles.carouselAuthor}>
            <div
              className={styles.carouselAvatar}
              style={{ background: t.color }}
            >
              {t.initial}
            </div>
            <div className={styles.carouselAuthorInfo}>
              <strong className={styles.carouselName}>{t.name}</strong>
              <span className={styles.carouselRole}>
                {t.role} · {t.company}
              </span>
            </div>
            <div className={styles.verifiedBadge}>✓ Verified</div>
          </div>
          <div className={styles.carouselControls}>
            <button className={styles.navBtn} onClick={prev}>
              ‹
            </button>
            <div className={styles.dots}>
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  className={`${styles.dot} ${i === active ? styles.dotActive : ""}`}
                  onClick={() => go(i, i > active ? "next" : "prev")}
                  style={i === active ? { background: t.color } : {}}
                />
              ))}
            </div>
            <button className={styles.navBtn} onClick={next}>
              ›
            </button>
          </div>
        </div>
        <div className={styles.sideCards}>
          {TESTIMONIALS.map((item, i) => (
            <button
              key={i}
              className={`${styles.sideCard} ${i === active ? styles.sideCardActive : ""}`}
              onClick={() => go(i, i > active ? "next" : "prev")}
              style={
                i === active
                  ? {
                    borderColor: item.color,
                    background: "rgba(3,191,203,0.04)",
                  }
                  : {}
              }
            >
              <div
                className={styles.sideAvatar}
                style={{ background: item.color }}
              >
                {item.initial}
              </div>
              <div className={styles.sideInfo}>
                <span className={styles.sideName}>{item.name}</span>
                <span className={styles.sideCompany}>{item.company}</span>
              </div>
              <span className={styles.sideMetric} style={{ color: item.color }}>
                {item.metric}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════
   HOME PAGE
══════════════════════════════ */
export default function HomePage() {
  useScrollReveal();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className={styles.page}>
      {/* ════════ 1. HERO ════════ */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay} />
        <div className={styles.orb1} />
        <div className={styles.orb2} />
        <div className={styles.orb3} />
        <div className="container">
          <div className={`row align-items-center ${styles.heroRow}`}>
            <div className="col-lg-6">
              {/* <span className={styles.heroTag}>
                B2B Outreach · SMB Specialists
              </span> */}
              <h1 className={styles.heroHeading}>
                We fill your funnel with the right conversations,{" "}
                <em>at scale</em>
              </h1>
              <p className={styles.heroSubhead}>
                Helping SMBs get consistent, high-quality meetings with
                decision-makers who matter.
              </p>
              <p className={styles.heroSupporting}>
                Email, calling, or both, powered by smart targeting and
                outcome-driven outreach.
              </p>
              <div className="reveal delay-4">
                <Link
                  to="/contact"
                  className="btn-primary-ie"
                  style={{ marginTop: "1.5rem" }}
                >
                  Talk to an Expert
                </Link>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-center">
              <div className={styles.heroVisual}>
                <div className={styles.visualRing1} />
                <div className={styles.visualRing2} />
                <div className={styles.visualCore}>
                  <span className={styles.coreEmoji}>
                    <FaCalendarAlt />
                  </span>
                </div>
                <div className={`${styles.orbitTag} ${styles.ot1}`}>
                  <span>✉️</span>
                  <span>Inbox to Invite</span>
                </div>
                <div className={`${styles.orbitTag} ${styles.ot2}`}>
                  <span>📞</span>
                  <span>Dials to Discovery Call</span>
                </div>
                <div className={`${styles.orbitTag} ${styles.ot3}`}>
                  <span>🔗</span>
                  <span>Full Spectrum Outreach </span>
                </div>
                <div className={`${styles.orbitTag} ${styles.ot4}`}>
                  <span>🚀</span>
                  <span>Reach at Scale</span>
                </div>
                <div className={`${styles.orbitTag} ${styles.ot5}`}>
                  <span>🎯</span>
                  <span>Outcome-Driven Outreach</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ SERVICES MARQUEE STRIP ════════ */}
      <section className={styles.servicesMarquee}>
        <div className={styles.marqueeServices}>
          <div className={styles.marqueeServicesTrack}>
            {[
              {
                icon: "✉️",
                title: "Inbox to Invite",
                desc: "Emailing that books real conversations",
              },
              {
                icon: "📞",
                title: "Dials to Discovery",
                desc: "Calling that opens real conversations",
              },
              {
                icon: "🔗",
                title: "Full Spectrum Outreach",
                desc: "High-quality emails & calls working together",
              },
              {
                icon: "🚀",
                title: "Reach at Scale",
                desc: "Book more meetings across every channel",
              },
              {
                icon: "🎯",
                title: "Outcome-Driven",
                desc: "You pay only for qualified results",
              },
              /* duplicate for seamless loop */
              {
                icon: "✉️",
                title: "Inbox to Invite",
                desc: "Emailing that books real conversations",
              },
              {
                icon: "📞",
                title: "Dials to Discovery",
                desc: "Calling that opens real conversations",
              },
              {
                icon: "🔗",
                title: "Full Spectrum Outreach",
                desc: "High-quality emails & calls working together",
              },
              {
                icon: "🚀",
                title: "Reach at Scale",
                desc: "Book more meetings across every channel",
              },
              {
                icon: "🎯",
                title: "Outcome-Driven",
                desc: "You pay only for qualified results",
              },
            ].map((s, i) => (
              <div key={i} className={styles.marqueeServiceItem}>
                <div className={styles.marqueeServiceIcon}>
                  <span>{s.icon}</span>
                </div>
                <div className={styles.marqueeServiceText}>
                  <strong>{s.title}</strong>
                  <span>{s.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ════════ CREDIBILITY STRIP ════════ */}
      <section className={styles.credStrip}>
        <div className="container">
          <p className={`${styles.credHeading} reveal`}>
            Built on real-world outreach experience
          </p>
          <div className={styles.credGrid}>
            {[
              {
                icon: <Handshake 
                style={{
                      
                      color: "#03bfcb",
                    }}
                />,
                stat: "15+",
                text: "Years of Revenue-Focused Client Engagement",
              },
              {
                icon: <Users 
                  style={{
                      
                      color: "#03bfcb",
                    }}
                />,
                stat: "1000+",
                text: "Qualified SMB Discovery Meetings",
              },
              {
                icon: <Rocket 
                style={{
                      
                      color: "#03bfcb",
                    }}
                />,
                stat: "1K+",
                text: "Strategic Outbound Campaigns Delivered",
              },
              {
                icon: <Globe2 
                style={{
                      
                      color: "#03bfcb",
                    }}
                />,
                stat: "",
                text: "Global Reach. Proven Impact.",
              },
              {
                icon: <Award 
                style={{
                      
                      color: "#03bfcb",
                    }}
                />,
                stat: "",
                text: "Proven Expertise Across Products and Services",
              },
            ].map((c, i) => (
              <div
                key={i}
                className={`${styles.credItem} reveal delay-${i + 1}`}
              >
                <span className={styles.credIcon}>{c.icon}</span>
                <span className={styles.credStat}>{c.stat}</span>
                <span className={styles.credText}>{c.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ 2. STATS ════════ */}
      <section className={styles.statsSection}>
        <div className="container">
          <div className={styles.statsRow}>
            <div className={styles.statTile}>
              <div className={styles.statCircle}>Market Research</div>
            </div>

            <div className={styles.statTile}>
              <div className={styles.statCircle}>Account Strategy</div>
            </div>

            <div className={styles.statTile}>
              <div className={styles.statCircle}>Outbound Motion</div>
            </div>

            <div className={styles.statTile}>
              <div className={styles.statCircle}>Measurable ROI</div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ 3. THE PROBLEM ════════ */}
      <section className={styles.problemSection}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-5 reveal-left">
              <div className={styles.problemVisual}>
                <div className={styles.problemOrb} />
                {[
                  {
                    icon: (
                      <ScanFace
                        size={28}
                        className="text-[#00A8E8]"
                        style={{
                          filter: "drop-shadow(0 0 8px rgba(0, 168, 232, 0.4))",
                        }}
                      />
                    ),
                    text: "Cold outreach feels noisy",
                  },
                  {
                    icon: (
                      <UserCheck
                        size={28}
                        className="text-[#C9FFC9]"
                        style={{
                          filter:
                            "drop-shadow(0 0 8px rgba(201, 255, 201, 0.4))",
                        }}
                      />
                    ),
                    text: "Leads aren't qualified",
                  },
                  {
                    icon: (
                      <CalendarX
                        size={28}
                        className="text-[#FF7F7F]"
                        style={{
                          filter:
                            "drop-shadow(0 0 8px rgba(255, 127, 127, 0.4))",
                        }}
                        aria-label="Empty Calendar Icon"
                      />
                    ),
                    text: "Calendars stay empty",
                  },
                  {
                    icon: (
                      <Timer
                        size={28}
                        className="text-[#FFB347]"
                        style={{
                          filter:
                            "drop-shadow(0 0 8px rgba(255, 179, 71, 0.4))",
                        }}
                        aria-label="Timer Icon"
                      />
                    ),
                    text: "Chasing instead of closing",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className={styles.painCard}
                    style={{ animationDelay: `${i * 0.2}s` }}
                  >
                    <span>{item.icon}</span>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-7">
              <span className="section-label reveal">The Problem</span>
              <h2 className={`${styles.sectionTitle} reveal delay-1`}>
                Most SMBs don't struggle with their services or products;
                <br />
                <em>they struggle with conversations.</em>
              </h2>
              <p className={`${styles.bodyText} reveal delay-2`}>
                You can have a great product or service, but without a steady
                flow of quality sales conversations, growth becomes
                unpredictable.
              </p>
              <p className={`${styles.bodyText} reveal delay-3`}>
                Stop Chasing prospects. Start converting the right ones.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ 4. THE SOLUTION ════════ */}
      <section className={styles.solutionSection}>
        <div className={styles.solutionBg} />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <span
                className="section-label reveal"
                style={{ color: "var(--primary)" }}
              >
                Our Solution
              </span>
              <h2 className={`${styles.sectionTitleLight} reveal delay-1`}>
                InviteEngine turns outreach into real sales conversations.
              </h2>
              <p className={`${styles.bodyTextLight} reveal delay-2`}>
                We help SMBs consistently connect with the right decision-makers
                through smart targeting, thoughtful outreach, and proven
                execution.
              </p>
              <p className={`${styles.bodyTextLight} reveal delay-3`}>
                No generic blasting. No vanity metrics. Just meaningful
                conversations that move your pipeline forward.
              </p>
              <div className="reveal delay-4">
                <Link
                  to="/contact"
                  className="btn-primary-ie"
                  style={{ marginTop: "1.5rem" }}
                >
                  Talk to an Expert
                </Link>
              </div>
            </div>
            <div className="col-lg-6 reveal-right">
              <div className={styles.solutionCards}>
                {[
                  {
                    // Vibrant Orange-Yellow for precision
                    icon: (
                      <Target
                        size={24}
                        className="text-[#FF9D42]"
                        style={{
                          color: "#FF9D42",
                          filter:
                            "drop-shadow(0 0 8px rgba(255, 157, 66, 0.4))",
                        }}
                        aria-label="Targeting Icon"
                      />
                    ),
                    title: "Smart Targeting",
                    desc: "We identify the exact people who need what you sell.",
                  },
                  {
                    // Bright Cyan/Blue for communication
                    icon: (
                      <PenTool
                        size={24}
                        className="text-[#38BDF8]"
                        style={{
                          color: "#38BDF8",
                          filter:
                            "drop-shadow(0 0 8px rgba(56, 189, 248, 0.4))",
                        }}
                        aria-label="Outreach Icon"
                      />
                    ),
                    title: "Thoughtful Outreach",
                    desc: "Human-written messages that earn real replies.",
                  },
                  {
                    // Electric Green for energy/execution
                    icon: (
                      <Zap
                        size={24}
                        className="text-[#4ADE80]"
                        style={{
                          color: "#4ADE80",
                          filter:
                            "drop-shadow(0 0 8px rgba(74, 222, 128, 0.4))",
                        }}
                        aria-label="Execution Icon"
                      />
                    ),
                    title: "Proven Execution",
                    desc: "Fast ramp, consistent delivery, full transparency.",
                  },
                  {
                    // Soft Purple/Lavender for scheduled meetings
                    icon: (
                      <CalendarCheck
                        size={24}
                        className="text-[#C084FC]"
                        style={{
                          color: "#C084FC",
                          filter:
                            "drop-shadow(0 0 8px rgba(192, 132, 252, 0.4))",
                        }}
                        aria-label="Meetings Icon"
                      />
                    ),
                    title: "Qualified Meetings",
                    desc: "Only conversations that match your criteria land on your calendar.",
                  },
                ].map((c, i) => (
                  <div
                    key={i}
                    className={`${styles.solutionCard} reveal delay-${i + 1}`}
                  >
                    <span className={styles.solutionIcon}>{c.icon}</span>
                    <div>
                      <h4 className={styles.solutionCardTitle}>{c.title}</h4>
                      <p className={styles.solutionCardDesc}>{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ 5. HOW IT WORKS ════════ */}
      <section className={styles.howSection}>
        <div className="container">
          <div className="text-center mb-5 reveal">
            <span className="section-label">Simple Process</span>
            <h2 className={styles.sectionTitle}>How InviteEngine Works</h2>
            <p className={styles.sectionSub}>
              From onboarding to booked meetings in days, not months.
            </p>
          </div>
          <div className={styles.stepsGrid}>
            {[
              {
                num: "01",
                icon: (
                  <Compass
                    size={32}
                    className="text-[#0D9488]" // Deep Teal
                    style={{
                      color: "#03bfcb",
                    }}
                    aria-label="Strategy and discovery icon"
                  />
                ),
                title: "Understand Your Business",
                desc: 'We learn your ICP, offer, and what a "good meeting" looks like for your team.',
              },
              {
                num: "02",
                icon: (
                  <Crosshair
                    size={32}
                    className="text-[#14b8a6]"
                    style={{

                      color: '#03bfcb',
                    }}
                  />
                ),
                title: "Target the Right Accounts",
                desc: "Smart targeting by industry, role, region, and intent signals.",
              },
              {
                num: "03",
                icon: (
                  <ShieldCheck
                    size={32}
                    className="text-[#5EEAD4]" // Lightest Teal
                    style={{

                      color: "#03bfcb",
                    }}
                    aria-label="Qualified meeting verification icon"
                  />
                ),
                title: "Run Outreach That Works",
                desc: "Email, calls, or both executed with quality or scale, based on your goals.",
              },
              {
                num: "04",
                icon: (
                  <CalendarCheck
                    size={32}
                    className="text-[#14b8a6]"
                    style={{

                      color: "#03bfcb",
                    }}
                  />
                ),
                title: "Book Qualified Meetings",
                desc: "We schedule discovery calls with the right prospects directly on your calendar.",

              },
            ].map((s, i) => (
              <div key={i} className={`${styles.step} reveal delay-${i + 1}`}>
                <div className={styles.stepNum}>{s.num}</div>
                <div className={styles.stepIcon}>{s.icon}</div>
                <h3 className={styles.stepTitle}>{s.title}</h3>
                <p className={styles.stepDesc}>{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-5 reveal">
            <Link to="/contact" className="btn-primary-ie">
              Let's Connect
            </Link>
          </div>
        </div>
      </section>

      {/* ════════ 6. SERVICES SNAPSHOT ════════ */}
      <section className={styles.servicesPreview}>
        <div className="container">
          <div className="text-center mb-5 reveal">
            <span className="section-label">Our Services</span>
            <h2 className={styles.sectionTitle}>
              One engine. Multiple ways to fuel your pipeline.
            </h2>
          </div>
          <div className="row g-4">
            {[
              {
                icon: <MailOpen size={32}
                  className="text-[#22D3EE]"
                  style={{

                    color: "#03bfcb",
                  }}
                />,
                title: "Inbox to Invite",
                desc: "Emailing that books real conversations.",
              },
              {
                icon: <PhoneCall size={32}
                  className="text-[#22D3EE]"
                  style={{

                    color: "#03bfcb",
                  }}
                />,

                title: "Dials to Discovery Calls",
                desc: "Calling that opens real conversations.",
              },
              {
                icon: <Layers size={32}
                  className="text-[#22D3EE]"
                  style={{

                    color: "#03bfcb",
                  }}
                />,
                title: "Full Spectrum Outreach",
                desc: "Email + calling, working together.",
              },
              {
                icon: <Rocket size={32}
                  className="text-[#22D3EE]"
                  style={{

                    color: "#03bfcb",
                  }}
                />,
                title: "Reach at Scale",
                desc: "More outreach, more meetings, faster.",
              },
              {
                icon: <Trophy size={32}
                  className="text-[#22D3EE]"
                  style={{

                    color: "#03bfcb",
                  }}
                />,
                title: "Outcome-Driven Outreach",
                desc: "Pay only for results.",
              },
            ].map((s, i) => (
              <div className="col-lg col-md-6" key={s.title}>
                <Link
                  to="/services"
                  className={`${styles.svcCard} reveal delay-${i + 1}`}
                >
                  <span className={styles.svcIcon}>{s.icon}</span>
                  <h3 className={styles.svcTitle}>{s.title}</h3>
                  <p className={styles.svcDesc}>{s.desc}</p>
                  <span className={styles.svcArrow}>Learn more →</span>
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-5 reveal">
            <Link to="/services" className="btn-dark-ie">
              Explore Our Services →
            </Link>
          </div>
        </div>
      </section>

      {/* ════════ 7. WHY INVITEENGINE ════════ */}
      <section className={styles.whySection}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-5 reveal-left">
              <span className="section-label">Why Us</span>
              <h2 className={styles.sectionTitle}>
                Why teams choose InviteEngine
              </h2>
              <p className={styles.sectionSub} style={{ textAlign: "left" }}>
                We're not another lead vendor. We're your outreach engine.
              </p>
              <Link
                to="/about"
                className="btn-dark-ie"
                style={{ marginTop: "1.5rem" }}
              >
                Learn About Us
              </Link>
            </div>
            <div className="col-lg-7">
              <div className={styles.whyGrid}>
                {[
                  {
                    icon: "💬",
                    title: "Conversations, not just leads",
                    desc: "We focus on getting you into real sales conversations, not inflating lead counts.",
                  },
                  {
                    icon: "🎯",
                    title: "Smart targeting over mass blasting",
                    desc: "Every outreach campaign is built around precision, the right people, right message.",
                  },
                  {
                    icon: "⚖️",
                    title: "Quality when it matters, scale when you need it",
                    desc: "We adapt our approach to your goals, whether that's careful ABM or high-volume coverage.",
                  },
                  {
                    icon: "💰",
                    title: "Outcome-driven options for ROI-focused teams",
                    desc: "Choose performance pricing and pay only for meetings that meet your agreed criteria.",
                  },
                  {
                    icon: "🏢",
                    title: "Built for SMBs, not enterprise complexity",
                    desc: "No bloated processes. Fast onboarding, clear reporting, real humans on your account.",
                  },
                  {
                    icon: "🌍",
                    title: "Global reach, local feel",
                    desc: "We run campaigns across all regions, with messaging adapted for each market. ",
                  },
                ].map((item, i) => (
                  <div
                    className={`${styles.whyCard} reveal delay-${(i % 3) + 1}`}
                    key={item.title}
                  >
                    <span className={styles.whyIcon}>{item.icon}</span>
                    <div>
                      <h4 className={styles.whyTitle}>{item.title}</h4>
                      <p className={styles.whyDesc}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ 8. WHO IT'S FOR ════════ */}
      <section className={styles.whoSection}>
        <div className="container">
          <div className="text-center mb-5 reveal">
            <span className="section-label">Who It's For</span>
            <h2 className={styles.sectionTitle}>Built for growing B2B teams</h2>
            <p className={styles.sectionSub}>
              If any of these sound like you, we should talk.
            </p>
          </div>
          <div className="row g-4 justify-content-center">
            {[
              {
                icon: <MonitorCheck size={40} strokeWidth={1.5} className="text-[#0D9488]" aria-label="B2B SaaS icon" style={{ color: "#03bfcb" }}/>,
                title: "B2B SaaS & Services",
                desc: "You have a strong product but need a consistent flow of qualified discovery calls.",
              },
              {
                icon: <UsersRound size={40} strokeWidth={1.5} className="text-[#0D9488]" aria-label="SMB Sales Teams icon" style={{ color: "#03bfcb" }} />,
                title: "SMB Sales Teams",
                desc: "Your reps are great closers, let us fill their calendars so they can do what they do best.",
              },
              {
                icon: <TrendingUp size={40} strokeWidth={1.5} className="text-[#0D9488]" aria-label="Founders & Growth Leaders icon" style={{ color: "#03bfcb" }}/>,
                title: "Founders & Growth Leaders",
                desc: "You want predictable pipeline without the overhead of building an in-house SDR team.",
              },
              {
                icon: <ShieldCheck size={40} strokeWidth={1.5} className="text-[#0D9488]" aria-label="Teams Tired of Bad Lead Vendors icon" style={{ color: "#03bfcb" }} />,
                title: "Teams Tired of Bad Lead Vendors",
                desc: "You've tried the generic databases and spray-and-pray tools. You're ready for something that actually works.",
              },
            ].map((w, i) => (
              <div className="col-lg-3 col-md-6" key={w.title}>
                <div className={`${styles.whoCard} reveal delay-${i + 1}`}>
                  <span className={styles.whoIcon}>{w.icon}</span>
                  <h3 className={styles.whoTitle}>{w.title}</h3>
                  <p className={styles.whoDesc}>{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ TOOLS / INTEGRATIONS ════════ */}
      <section className={styles.toolsImageSection}>
        <div className="container">
          <div className="text-center mb-5 reveal">
            <span className="section-label">Tools Expertise</span>
            <h2 className={styles.sectionTitle}>
              Expertise Across the Modern Sales Tech Stack
            </h2>
            {/* <p className={styles.sectionSub}>
        Expertise Across the Modern Sales Tech Stack
      </p> */}
          </div>

          <div className={styles.toolsImageWrap}>
            <img
              src="/tools-logos.jpeg"
              alt="Sales tools we work with"
              className={styles.toolsImage}
            />
          </div>
        </div>
      </section>

      <IndustriesHome />

      {/* ════════ 9. FINAL CTA ════════ */}
      <section className={styles.finalCta}>
        <div className={styles.finalCtaBg} />
        <div
          className="container"
          style={{ position: "relative", zIndex: 1, textAlign: "center" }}
        >
          <span className={`${styles.ctaTag} reveal`}>Ready to grow?</span>
          <h2 className={`${styles.finalCtaHeading} reveal delay-1`}>
            Ready to fill your funnel with the
            <br />
            <em>right conversations?</em>
          </h2>
          <p className={`${styles.finalCtaSub} reveal delay-2`}>
            No pressure. No pitch. Just a quick conversation to see if
            InviteEngine is a fit.
          </p>
          <div className="reveal delay-3">
            <Link
              to="/contact"
              className="btn-primary-ie"
              style={{
                marginRight: "1rem",
                fontSize: "0.9rem",
                padding: "1rem 2.5rem",
              }}
            >
              Schedule a Free Consultation
            </Link>
            <Link to="/services" className="btn-outline-ie">
              Explore Services
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
